import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { Box, CloseButton, Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppPagination from "@/features/line-sheet-sets/app-pagination";
import AppOrderSheetStatusLabel from "@/features/ui/app-order-sheet-status-label";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import CenteredSpinner from "@/shared/components/centered-spinner";
import OrderHistoryInquiryContent from "@/features/my-order/features/order-history/components/inquiry-section/OrderHistoryInquiryContent";
import useGetAgencyInquiryThreads, {
  AGENCY_INQUIRY_PAGE_SIZE,
} from "@/features/inquiries/hooks/useGetAgencyInquiryThreads";
import {
  AgencyInquiryFilterType,
  AgencyInquiryThread,
} from "@/features/inquiries/types";

const FILTERS: {
  value: AgencyInquiryFilterType;
  label: string;
  countKey: "answer_required_count" | "unread_thread_count" | "all_count";
}[] = [
  {
    value: "ANSWER_REQUIRED",
    label: "답변 필요",
    countKey: "answer_required_count",
  },
  { value: "UNREAD", label: "안 읽음", countKey: "unread_thread_count" },
  { value: "ALL", label: "전체", countKey: "all_count" },
];

const ROLE_LABEL: Record<string, string> = {
  BUYER: "바이어",
  AGENCY: "에이전시",
  BOUTIQUE: "부티크",
};

// 바이어 아이콘 배경 — 바이어 id로 고정해 같은 바이어는 늘 같은 색으로 보인다
const AVATAR_COLORS = [
  "#FDE2D4",
  "#FCEFC7",
  "#DDF0D8",
  "#D8E9F8",
  "#E6DDF6",
  "#F6DCE6",
];

const SELECTED_PARAM = "order_sheet_set_id";

// 목록 날짜 — 채널톡처럼 날짜만. 올해는 MM-DD, 지난해 이전은 YYYY-MM-DD
function formatThreadDate(value: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  const pad = (n: number) => String(n).padStart(2, "0");
  const monthDay = `${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return date.getFullYear() === new Date().getFullYear()
    ? monthDay
    : `${date.getFullYear()}-${monthDay}`;
}

// 에이전시 문의함 — 채널톡처럼 왼쪽에 문의가 있는 오더 목록, 오른쪽에 고른 오더의 문의 대화.
// 오더 목록은 제출 전 오더를 빼고 보여 주지만, 문의함은 오더 상태와 상관없이 다 모은다.
// 고른 오더는 주소(?order_sheet_set_id=)에 남겨 새로고침·링크 공유에도 같은 대화가 열린다.
export default function AgencyInquiryListPage() {
  const { navigate } = useRouterHelper();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterType, setFilterType] =
    useState<AgencyInquiryFilterType>("ANSWER_REQUIRED");
  const [pageNumber, setPageNumber] = useState(1);

  const selectedId = Number(searchParams.get(SELECTED_PARAM)) || null;
  // 답변·읽음으로 목록에서 빠져도(답변 필요·안 읽음 탭) 머리줄 정보가 남도록 마지막으로 본 줄을 기억한다
  const [selectedThread, setSelectedThread] =
    useState<AgencyInquiryThread | null>(null);

  const { data, isPending } = useGetAgencyInquiryThreads({
    filterType,
    pageNumber,
  });
  const threads = data?.threads ?? [];

  const selectThread = (id: number | null, replace = false) => {
    const next = new URLSearchParams(searchParams);
    if (id) {
      next.set(SELECTED_PARAM, String(id));
    } else {
      next.delete(SELECTED_PARAM);
    }
    setSearchParams(next, { replace });
  };

  // 목록이 새로 오면 고른 줄 정보를 최신으로 맞추고, 아무것도 안 골랐으면(넓은 화면) 맨 위 대화를 연다
  useEffect(() => {
    if (!data) return;
    if (selectedId) {
      const found = data.threads.find(
        (thread) => thread.order_sheet_set_id === selectedId
      );
      if (found) setSelectedThread(found);
      return;
    }
    const isWide = window.matchMedia("(min-width: 48em)").matches;
    if (isWide && data.threads.length > 0) {
      selectThread(data.threads[0].order_sheet_set_id, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedId]);

  const header =
    selectedThread?.order_sheet_set_id === selectedId ? selectedThread : null;

  return (
    <>
      <Helmet>
        <title>Pathrade | Inquiries</title>
      </Helmet>
      <Flex height={"100%"} minHeight={0} background={"#FFFFFF"}>
        {/* 왼쪽: 문의 목록 — 좁은 화면에서는 대화를 고르면 숨긴다 */}
        <Flex
          display={{ base: selectedId ? "none" : "flex", md: "flex" }}
          flexDirection={"column"}
          width={{ base: "100%", md: "380px" }}
          flexShrink={0}
          minHeight={0}
          borderRight={"0.5px solid #E8E8E5"}
        >
          <Flex
            flexDirection={"column"}
            gap={"12px"}
            padding={"20px 16px 12px"}
          >
            <Text textStyle={"heading-md"} color={"#1F1F1F"}>
              문의/답변
            </Text>
            <Flex gap={"6px"}>
              {FILTERS.map((filter) => {
                const isActive = filterType === filter.value;
                return (
                  <Box
                    as={"button"}
                    key={filter.value}
                    padding={"4px 12px"}
                    borderRadius={"full"}
                    background={isActive ? "#EFEFED" : "transparent"}
                    boxShadow={isActive ? "0 0 0 0.5px #D4D4D1" : "none"}
                    onClick={() => {
                      setFilterType(filter.value);
                      setPageNumber(1);
                    }}
                  >
                    <Text
                      textStyle={isActive ? "body-sm-bold" : "body-sm-regular"}
                      color={isActive ? "#1F1F1F" : "#6F6F6C"}
                    >
                      {filter.label}
                      {data ? ` ${data[filter.countKey]}` : ""}
                    </Text>
                  </Box>
                );
              })}
            </Flex>
          </Flex>

          <Box flex={1} minHeight={0} overflowY={"auto"} paddingX={"8px"}>
            {isPending ? (
              <CenteredSpinner />
            ) : threads.length === 0 ? (
              <Flex padding={"40px 16px"} justifyContent={"center"}>
                <Text textStyle={"body-md-regular"} color={"#8F8F8C"}>
                  {filterType === "ANSWER_REQUIRED"
                    ? "답변이 필요한 문의가 없습니다."
                    : filterType === "UNREAD"
                    ? "안 읽은 문의가 없습니다."
                    : "등록된 문의가 없습니다."}
                </Text>
              </Flex>
            ) : (
              threads.map((thread) => (
                <InquiryThreadRow
                  key={thread.order_sheet_set_id}
                  thread={thread}
                  isSelected={thread.order_sheet_set_id === selectedId}
                  onClick={() => {
                    setSelectedThread(thread);
                    selectThread(thread.order_sheet_set_id);
                  }}
                />
              ))
            )}
          </Box>

          {(data?.total_count ?? 0) > AGENCY_INQUIRY_PAGE_SIZE && (
            <Flex
              justifyContent={"center"}
              paddingY={"8px"}
              borderTop={"0.5px solid #E8E8E5"}
            >
              <AppPagination
                capacity={5}
                pageSize={AGENCY_INQUIRY_PAGE_SIZE}
                totalCount={data?.total_count}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
              />
            </Flex>
          )}
        </Flex>

        {/* 오른쪽: 고른 오더의 문의 대화 */}
        <Flex
          display={{ base: selectedId ? "flex" : "none", md: "flex" }}
          flex={1}
          minWidth={0}
          minHeight={0}
          flexDirection={"column"}
        >
          {selectedId ? (
            <>
              <Flex
                alignItems={"center"}
                gap={"12px"}
                padding={"14px 20px"}
                borderBottom={"0.5px solid #E8E8E5"}
              >
                <CloseButton
                  display={{ base: "flex", md: "none" }}
                  size={"sm"}
                  onClick={() => selectThread(null)}
                />
                <Flex
                  flexDirection={"column"}
                  gap={"2px"}
                  flex={1}
                  minWidth={0}
                >
                  <Text
                    textStyle={"body-lg-bold"}
                    color={"#1F1F1F"}
                    noOfLines={1}
                  >
                    {header?.buyer?.name ?? `주문번호 ${selectedId}`}
                  </Text>
                  {header && (
                    <Flex alignItems={"center"} gap={"8px"} minWidth={0}>
                      <Text
                        textStyle={"body-sm-regular"}
                        color={"#8F8F8C"}
                        noOfLines={1}
                      >
                        {header.line_sheet_set.name} · #
                        {header.order_sheet_set_id}
                      </Text>
                      <Box flexShrink={0}>
                        <AppOrderSheetStatusLabel
                          value={header.order_sheet_set_status}
                        />
                      </Box>
                    </Flex>
                  )}
                </Flex>
                {header && (
                  <AppButton
                    variant={"white"}
                    onClick={() => {
                      navigate(
                        `/line-sheet-sets/${header.line_sheet_set.id}?order_sheet_set_id=${header.order_sheet_set_id}&tab=INQUIRY`
                      );
                    }}
                  >
                    오더 상세
                  </AppButton>
                )}
              </Flex>
              <Flex
                flex={1}
                minHeight={0}
                flexDirection={"column"}
                padding={"12px 20px 16px"}
              >
                <React.Suspense fallback={<CenteredSpinner />}>
                  {/* key: 오더를 바꾸면 새로 마운트해 입력 초안·읽음 처리 기록이 이전 오더에서 넘어오지 않게 한다 */}
                  <OrderHistoryInquiryContent
                    key={selectedId}
                    orderSheetSetId={selectedId}
                    fillHeight={true}
                  />
                </React.Suspense>
              </Flex>
            </>
          ) : (
            <Flex flex={1} alignItems={"center"} justifyContent={"center"}>
              <Text textStyle={"body-md-regular"} color={"#8F8F8C"}>
                {isPending ? "" : "왼쪽에서 문의를 골라 주세요."}
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  );
}

function InquiryThreadRow({
  thread,
  isSelected,
  onClick,
}: {
  thread: AgencyInquiryThread;
  isSelected: boolean;
  onClick: () => void;
}) {
  const last = thread.last_inquiry;
  const hasUnread = thread.unread_count > 0;
  const buyerName = thread.buyer?.name ?? "-";
  const authorLabel =
    last.author_company_type === "AGENCY"
      ? "Me"
      : ROLE_LABEL[last.author_company_type] ?? last.author_company_type;
  const preview = last.body || (last.attachment_count > 0 ? "(첨부파일)" : "-");

  return (
    <Flex
      padding={"12px"}
      gap={"12px"}
      borderRadius={"8px"}
      cursor={"pointer"}
      background={isSelected ? "#F2F2F0" : "transparent"}
      _hover={{ background: isSelected ? "#F2F2F0" : "#F7F7F5" }}
      onClick={onClick}
    >
      <Flex
        width={"40px"}
        height={"40px"}
        flexShrink={0}
        borderRadius={"full"}
        alignItems={"center"}
        justifyContent={"center"}
        background={
          AVATAR_COLORS[(thread.buyer?.id ?? 0) % AVATAR_COLORS.length]
        }
      >
        <Text textStyle={"body-md-bold"} color={"#444444"}>
          {buyerName.trim().charAt(0).toUpperCase()}
        </Text>
      </Flex>
      <Flex flexDirection={"column"} gap={"2px"} flex={1} minWidth={0}>
        <Flex alignItems={"baseline"} gap={"8px"} minWidth={0}>
          <Text
            textStyle={"body-md-semi-bold"}
            color={"#1F1F1F"}
            noOfLines={1}
            flex={1}
            minWidth={0}
          >
            {buyerName}
          </Text>
          <Text textStyle={"caption-regular"} color={"#8F8F8C"} flexShrink={0}>
            {formatThreadDate(last.created_at)}
          </Text>
        </Flex>
        <Text textStyle={"caption-regular"} color={"#8F8F8C"} noOfLines={1}>
          {thread.line_sheet_set.name} · #{thread.order_sheet_set_id}
        </Text>
        <Flex alignItems={"center"} gap={"6px"} minWidth={0}>
          <Text
            textStyle={hasUnread ? "body-sm-medium" : "body-sm-regular"}
            color={hasUnread ? "#1F1F1F" : "#6F6F6C"}
            noOfLines={1}
            flex={1}
            minWidth={0}
          >
            {authorLabel}: {preview}
          </Text>
          {thread.is_answer_required && (
            <Text textStyle={"caption-bold"} color={"#CC1D1D"} flexShrink={0}>
              답변 필요
            </Text>
          )}
          {hasUnread && (
            <Flex
              minWidth={"18px"}
              height={"18px"}
              paddingX={"5px"}
              flexShrink={0}
              borderRadius={"full"}
              background={"#FF6B35"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Text
                fontSize={"11px"}
                fontWeight={"700"}
                lineHeight={1}
                color={"#FFFFFF"}
                sx={{ textBox: "trim-both cap alphabetic" }}
              >
                {thread.unread_count > 99 ? "99+" : thread.unread_count}
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
